/**
 * Text formatting utilities
 */

import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, TextFormat } from "./types";

/**
 * Check if a value should be hidden based on format rules
 */
const shouldHideValue = (value: FieldValue, format: { hideWhenNull?: boolean; hideWhenZero?: boolean }): boolean => {
  if (format.hideWhenNull && (value === null || value === undefined)) {
    return true;
  }
  if (format.hideWhenZero && value === 0) {
    return true;
  }
  return false;
};

/**
 * Convert text to uppercase
 */
export const toUpperCase = (text: string | null | undefined): string => {
  return text?.toUpperCase() ?? "";
};

/**
 * Convert text to lowercase
 */
export const toLowerCase = (text: string | null | undefined): string => {
  return text?.toLowerCase() ?? "";
};

/**
 * Convert text to sentence case (first letter capitalized, rest lowercase).
 *
 * @param text - Text to convert
 * @returns Sentence-cased text
 *
 * @example
 * ```typescript
 * toSentenceCase("HELLO WORLD"); // "Hello world"
 * toSentenceCase("hello world"); // "Hello world"
 * ```
 */
export const toSentenceCase = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Convert text to title case with proper handling of articles and prepositions.
 * Capitalizes the first letter of each word except common articles/prepositions
 * (unless they're the first word).
 *
 * @param text - Text to convert
 * @returns Title-cased text
 *
 * @example
 * ```typescript
 * toTitleCase("the quick brown fox"); // "The Quick Brown Fox"
 * toTitleCase("war and peace"); // "War and Peace"
 * toTitleCase("to be or not to be"); // "To Be or Not to Be"
 * ```
 */
export const toTitleCase = (text: string | null | undefined): string => {
  if (!text) return "";
  const articles = new Set([
    "a",
    "an",
    "the",
    "and",
    "but",
    "or",
    "for",
    "nor",
    "on",
    "at",
    "of",
    "to",
    "with",
    "in",
    "as",
    "by",
  ]);

  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => (articles.has(word) && index !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
};

/**
 * Capitalize first letter of each word
 */
export const capitalize = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Uppercase first letter only
 */
export const ucFirst = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Convert camelCase to words
 */
export const camelCaseToWords = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Convert snake_case to words
 */
export const snakeCaseToWords = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split("_")
    .map((word) => ucFirst(word))
    .join(" ");
};

/**
 * Convert enum-style text to sentence case
 */
export const enumToSentenceCase = (text: string | null | undefined): string => {
  if (!text) return "";

  // Handle snake_case
  if (text.includes("_")) {
    return snakeCaseToWords(text);
  }

  // Handle SCREAMING_CASE
  if (text.toUpperCase() === text) {
    return ucFirst(text.toLowerCase());
  }

  // Handle camelCase
  return camelCaseToWords(text);
};

/**
 * Truncate text in the middle
 */
export const truncateMiddle = (value: string | null | undefined, length: number = 16): string => {
  if (!value) return "";

  // Handle invalid length
  if (length < 5) return value;

  // Handle case where specified length is longer than string
  if (value.length <= length) return value;

  const totalChars = length - 3; // Account for ...
  const leadingChars = Math.ceil(totalChars / 2);
  const trailingChars = Math.floor(totalChars / 2);

  return `${value.slice(0, leadingChars)}...${value.slice(-trailingChars)}`;
};

/**
 * Truncate text at the start
 */
export const truncateStart = (value: string | null | undefined, length: number = 8): string => {
  if (!value) return "";
  if (value.length <= length) return value;
  return `...${value.slice(-length)}`;
};

/**
 * Truncate text at the end
 */
export const truncateEnd = (
  value: string | null | undefined,
  length: number = 16,
  ellipsis: string = "..."
): string => {
  if (!value) return "";
  if (value.length <= length) return value;
  return value.slice(0, length) + ellipsis;
};

/**
 * Mask sensitive text (e.g., secrets, API keys)
 */
export const toSecret = (value: string | null | undefined): string => {
  if (!value) return "";
  const str = value.toString();
  const lastFour = str.slice(-4);
  const maskedLength = str.length - 4;
  return maskedLength > 0 ? "*".repeat(maskedLength) + lastFour : str;
};

/**
 * Format text according to TextFormat specification
 */
export const formatText = (value: FieldValue, format: TextFormat): FormattedValue => {
  if (shouldHideValue(value, format)) return null;
  if (value == null) return format.nullText ?? null;

  let formatted = String(value);

  // Apply transforms
  if (format.transform) {
    const transforms = Array.isArray(format.transform) ? format.transform : [format.transform];

    transforms.forEach((transform) => {
      switch (transform) {
        case "uppercase":
          formatted = toUpperCase(formatted);
          break;
        case "lowercase":
          formatted = toLowerCase(formatted);
          break;
        case "capitalize":
          formatted = capitalize(formatted);
          break;
        case "titleCase":
          formatted = toTitleCase(formatted);
          break;
        case "sentenceCase":
          formatted = toSentenceCase(formatted);
          break;
      }
    });
  }

  // Apply truncation
  if (format.truncate) {
    const { length, position, ellipsis = "..." } = format.truncate;
    switch (position) {
      case "start":
        formatted = truncateStart(formatted, length);
        break;
      case "middle":
        formatted = truncateMiddle(formatted, length);
        break;
      case "end":
        formatted = truncateEnd(formatted, length, ellipsis);
        break;
    }
  }

  return formatted;
};

// Register text formatters
FormatRegistry.register("uppercase", (value) => toUpperCase(String(value)));
FormatRegistry.register("lowercase", (value) => toLowerCase(String(value)));
FormatRegistry.register("sentenceCase", (value) => toSentenceCase(String(value)));
FormatRegistry.register("titleCase", (value) => toTitleCase(String(value)));
FormatRegistry.register("capitalize", (value) => capitalize(String(value)));
FormatRegistry.register("camelCaseToWords", (value) => camelCaseToWords(String(value)));
FormatRegistry.register("snakeCaseToWords", (value) => snakeCaseToWords(String(value)));
FormatRegistry.register("enumToSentenceCase", (value) => enumToSentenceCase(String(value)));
FormatRegistry.register("truncateMiddle", (value, length) =>
  truncateMiddle(String(value), typeof length === "number" ? length : 16)
);
FormatRegistry.register("truncateStart", (value, length) =>
  truncateStart(String(value), typeof length === "number" ? length : 8)
);
FormatRegistry.register("truncateEnd", (value, length, ellipsis) =>
  truncateEnd(String(value), typeof length === "number" ? length : 16, typeof ellipsis === "string" ? ellipsis : "...")
);
FormatRegistry.register("secret", (value) => toSecret(String(value)));
